import { gql } from '@apollo/client';
import { client } from 'graphql/client';

export type AddressCoordDedupJobType =
    | 'IMPORT'
    | 'VALIDATION'
    | 'UPDATE_ADDRESS_CORE'
    | 'ROLLBACK_ADDRESS_CORE';

export type AddressCoordDedupJobStatus =
    | 'RUNNING'
    | 'CANCEL_REQUESTED'
    | 'CANCELLED'
    | 'SUCCESS'
    | 'FAILED';

export type AddressCoordDedupDbFilter = {
    field: string;
    values: string[];
};

export type AddressCoordDedupExecutionRequest = {
    fullImport: boolean;
    importBatchSize: number;
    validationBatchSize: number;
    updateBatchSize: number;
    limit: number;
    maxGoogleCalls: number;
    minGoogleCallIntervalMs: number;
    dataSource: string;
    validationRuleVersion: string;
    dbFilter?: AddressCoordDedupDbFilter | null;
};

export type AddressCoordDedupExecutionSummary = {
    batchId: string;
    importScannedCount: number;
    importInsertedCount: number;
    importSkippedCount: number;
    importedMaxSourceId?: number | null;
    validatedCandidateCount: number;
    updateScannedCount: number;
    updateAppliedCount: number;
    updateFailedCount: number;
    updateSkippedCount: number;
    rollbackScannedCount: number;
    rollbackAppliedCount: number;
    rollbackFailedCount: number;
    resolvedCount: number;
    failedCount: number;
    skippedCount: number;
    cacheHitCount: number;
    googleCallCount: number;
    groupShortcutSkippedCount: number;
    failureReasonCounts: Record<string, number>;
    skipReasonCounts: Record<string, number>;
    updateFailureReasonCounts?: Record<string, number>;
    groupShortcutSkippedGroupNos?: number[];
    executionDurationMs: number;
};

export type AddressCoordDedupJobSnapshot = {
    jobId: string;
    type: AddressCoordDedupJobType;
    status: AddressCoordDedupJobStatus;
    zipcode?: string | null;
    batchId?: string | null;
    startedAt: string;
    finishedAt?: string | null;
    successCount: number;
    progress?: AddressCoordDedupJobProgress | null;
    summary?: AddressCoordDedupExecutionSummary | null;
    errorMessage?: string | null;
    sqlFileName?: string | null;
    sqlDownloadUrl?: string | null;
};

export type AddressCoordDedupJobProgress = {
    current: number;
    total?: number | null;
    percent?: number | null;
    successCount: number;
    message: string;
    updatedAt: string;
    processedPerSecond?: number | null;
    estimatedRemainingSeconds?: number | null;
};

type JobListResponse = {
    jobs: AddressCoordDedupJobSnapshot[];
};

type GraphQLReasonCount = {
    reason: string;
    count: number;
};

type GraphQLExecutionSummary = Omit<
    AddressCoordDedupExecutionSummary,
    'failureReasonCounts' | 'skipReasonCounts' | 'updateFailureReasonCounts'
> & {
    failureReasonCounts: GraphQLReasonCount[];
    skipReasonCounts: GraphQLReasonCount[];
    updateFailureReasonCounts: GraphQLReasonCount[];
};

type GraphQLJobSnapshot = Omit<AddressCoordDedupJobSnapshot, 'summary'> & {
    summary?: GraphQLExecutionSummary | null;
};

const ADDRESS_COORD_DEDUP_JOB_FIELDS = gql`
  fragment AddressCoordDedupJobFields on AddressCoordDedupJobSnapshotType {
    jobId
    type
    status
    zipcode
    batchId
    startedAt
    finishedAt
    successCount
    errorMessage
    sqlFileName
    sqlDownloadUrl
    progress {
      current
      total
      percent
      successCount
      message
      updatedAt
      processedPerSecond
      estimatedRemainingSeconds
    }
    summary {
      batchId
      importScannedCount
      importInsertedCount
      importSkippedCount
      importedMaxSourceId
      validatedCandidateCount
      updateScannedCount
      updateAppliedCount
      updateFailedCount
      updateSkippedCount
      rollbackScannedCount
      rollbackAppliedCount
      rollbackFailedCount
      resolvedCount
      failedCount
      skippedCount
      cacheHitCount
      googleCallCount
      groupShortcutSkippedCount
      failureReasonCounts {
        reason
        count
      }
      skipReasonCounts {
        reason
        count
      }
      updateFailureReasonCounts {
        reason
        count
      }
      groupShortcutSkippedGroupNos
      executionDurationMs
    }
  }
`;

const LIST_ADDRESS_COORD_DEDUP_JOBS = gql`
  query AddressCoordDedupJobs {
    addressCoordDedupJobs {
      jobs {
        ...AddressCoordDedupJobFields
      }
    }
  }
  ${ADDRESS_COORD_DEDUP_JOB_FIELDS}
`;

const START_ADDRESS_COORD_DEDUP_JOB = gql`
  mutation StartAddressCoordDedupJob($input: StartAddressCoordDedupJobInput!) {
    startAddressCoordDedupJob(input: $input) {
      ...AddressCoordDedupJobFields
    }
  }
  ${ADDRESS_COORD_DEDUP_JOB_FIELDS}
`;

const CANCEL_ADDRESS_COORD_DEDUP_JOB = gql`
  mutation CancelAddressCoordDedupJob($jobId: String!) {
    cancelAddressCoordDedupJob(jobId: $jobId) {
      ...AddressCoordDedupJobFields
    }
  }
  ${ADDRESS_COORD_DEDUP_JOB_FIELDS}
`;

function reasonCountsToRecord(counts?: GraphQLReasonCount[] | null): Record<string, number> {
    return (counts || []).reduce<Record<string, number>>((acc, item) => {
        acc[item.reason] = item.count;
        return acc;
    }, {});
}

function normalizeJob(job: GraphQLJobSnapshot): AddressCoordDedupJobSnapshot {
    if (!job.summary) {
        return job as AddressCoordDedupJobSnapshot;
    }

    return {
        ...job,
        summary: {
            ...job.summary,
            failureReasonCounts: reasonCountsToRecord(job.summary.failureReasonCounts),
            skipReasonCounts: reasonCountsToRecord(job.summary.skipReasonCounts),
            updateFailureReasonCounts: reasonCountsToRecord(job.summary.updateFailureReasonCounts),
        },
    };
}

export function listAddressCoordDedupJobs(): Promise<JobListResponse> {
    return client
        .query<{ addressCoordDedupJobs: { jobs: GraphQLJobSnapshot[] } }>({
            query: LIST_ADDRESS_COORD_DEDUP_JOBS,
            fetchPolicy: 'network-only',
        })
        .then(result => ({
            jobs: result.data.addressCoordDedupJobs.jobs.map(normalizeJob),
        }));
}

export function startAddressCoordDedupJob(
    type: AddressCoordDedupJobType,
    request: AddressCoordDedupExecutionRequest
): Promise<AddressCoordDedupJobSnapshot> {
    return client
        .mutate<{ startAddressCoordDedupJob: GraphQLJobSnapshot }>({
            mutation: START_ADDRESS_COORD_DEDUP_JOB,
            variables: {
                input: {
                    type,
                    request,
                },
            },
        })
        .then(result => {
            if (!result.data?.startAddressCoordDedupJob) {
                throw new Error('启动任务失败');
            }
            return normalizeJob(result.data.startAddressCoordDedupJob);
        });
}

export function cancelAddressCoordDedupJob(jobId: string): Promise<AddressCoordDedupJobSnapshot> {
    return client
        .mutate<{ cancelAddressCoordDedupJob: GraphQLJobSnapshot }>({
            mutation: CANCEL_ADDRESS_COORD_DEDUP_JOB,
            variables: { jobId },
        })
        .then(result => {
            if (!result.data?.cancelAddressCoordDedupJob) {
                throw new Error('中断任务失败');
            }
            return normalizeJob(result.data.cancelAddressCoordDedupJob);
        });
}
